import { useEffect, useState } from 'react';
import useSalesBotStore from '@/stores/SalesBotStore';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';

import { saveAutopilotPrompt } from '@/hooks/sales-bot';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import AutopilotDemo from '@/components/autopilot/AutopilotDemo';

const Autopilot = () => {
  const [promptValue, setPromptValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const { systemPrompt, updateSystemPrompt } = useSalesBotStore(
    useShallow((state) => ({
      systemPrompt: state.systemPrompt,
      updateSystemPrompt: state.updateSystemPrompt,
    }))
  );

  // Initialize prompt value from store when it's available
  useEffect(() => {
    if (systemPrompt) {
      setPromptValue(systemPrompt);
    }
  }, [systemPrompt]);

  const handleSavePrompt = async () => {
    try {
      if (promptValue.trim() === '') {
        toast.error('Please enter a prompt');
        return;
      }

      setIsSaving(true);
      await toast.promise(saveAutopilotPrompt(promptValue), {
        loading: 'Saving prompt...',
        success: () => {
          updateSystemPrompt(promptValue);
          setIsSaving(false);
          return 'Prompt saved successfully';
        },
        error: (error) => {
          setIsSaving(false);
          return error.response?.data?.message || 'Failed to save prompt';
        },
      });
    } catch (error) {
      toast.error('Failed to save prompt');
      setIsSaving(false);
    }
  };

  return (
    <div className='flex h-full flex-col overflow-hidden lg:flex-row'>
      {/* Left Half - Prompt Configuration */}
      <div className='flex w-full flex-col border-b border-r-0 p-2 pb-4 lg:w-1/2 lg:border-b-0 lg:border-r lg:p-4'>
        <div className='mb-2'>
          <div className='space-y-1 text-xs text-muted-foreground'>
            <p>If you don&apos;t have a prompt, follow these steps:</p>
            <ol className='ml-4 list-decimal space-y-0.5'>
              <li>
                Go to{' '}
                <a
                  href='https://chatgpt.com/g/g-68fe58062de881919725088d1e7d86ec-revio-copilot-prompt-generation'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-primary underline hover:text-primary/80'
                >
                  Revio CustomGPT
                </a>
              </li>
              <li>Answer all the questions (usually takes 30 mins)</li>
              <li>At the end it will output a prompt</li>
              <li>Copy & paste the prompt here</li>
            </ol>
          </div>
        </div>
        <div className='flex flex-col'>
          <Textarea
            value={promptValue}
            onChange={(e) => setPromptValue(e.target.value)}
            placeholder='Enter your autopilot prompt here...'
            className='custom-scrollbar mb-2 h-[63vh] max-h-[100vh] resize-none'
          />
          <Button
            onClick={handleSavePrompt}
            disabled={isSaving || promptValue.trim() === ''}
            className='w-64 self-end'
          >
            {isSaving ? 'Saving...' : 'Save Prompt'}
          </Button>
        </div>
      </div>

      {/* Right Half - Demo Bot */}
      <AutopilotDemo />
    </div>
  );
};

export default Autopilot;
